import { NetworkAssetsStep } from "#batteries-included-components/Forms/NetworkForm/NetworkAssetsStep";
import { NetworkDetailsStep } from "#batteries-included-components/Forms/NetworkForm/NetworkDetailsStep";
import { useCreateNetwork } from "#hooks/adapters/useNetworks";
import { useNavigate, useParams } from "#routers/hooks";
import { NetworkListRoute } from "#routes/organization/networks";
import { NetworkDetailRoute } from "#routes/organization/networks/[networkId]/detail";
import { NetworkCreateRoute } from "#routes/organization/networks/create/index";
import {
  AssetCreateMode,
  AssetCreateModeType,
} from "#src/batteries-included-components/Buttons/CreateAssetDropdownMenu/CreateAssetDropdownMenu";
import { NetworkOverviewFormStep } from "#src/batteries-included-components/Forms/NetworkForm/NetworkOverviewStep";
import { useListCustomAttributes } from "#src/components/hooks/adapters/useCustomAttributes";
import { useIsFeatureAvailable } from "#src/contexts/AuthenticatedContext.helpers";
import useLocalization from "#src/hooks/useLocalization";
import {
  MultiStepFormProvider,
  useMultiStepFormContext,
} from "#src/hooks/useMultiStepForm";
import { sanitizeCustomAttributeFormInputs } from "#src/utils/customAttributes";
import { useBreadcrumbsFromRoute } from "#utils/route";
import { Page, Panel } from "@validereinc/common-components";
import {
  AssetType,
  type AssetGroupCreateOneRequestType,
  type AssetGroupType,
} from "@validereinc/domain";
import classNames from "classnames/bind";
import React from "react";
import styles from "./CreateNetworkPage.module.scss";

const cx = classNames.bind(styles);

export const CreateNetworkPageContent = ({
  shouldUseAssetBuilder,
}: {
  shouldUseAssetBuilder: boolean;
}) => {
  const { localize } = useLocalization();
  const { stepper, footer, currentStep } = useMultiStepFormContext();

  const [breadcrumbs] = useBreadcrumbsFromRoute(NetworkCreateRoute);

  return (
    <Page
      title={`${localize(NetworkCreateRoute.title)}`}
      category={`Quick Create ${localize(AssetType.ASSET_GROUP)}`}
      breadcrumbs={breadcrumbs}
      shouldCenterContent
      shouldContentBeFullWidth
      footer={footer}
    >
      <div className={cx("container")}>
        <Panel
          className={cx("panel")}
          isFluidY={false}
        >
          <div className={cx("stepper-container")}>{stepper}</div>
          <div
            className={cx("form-container", currentStep === 3 && "should-grow")}
          >
            <NetworkOverviewFormStep
              shouldUseAssetBuilder={shouldUseAssetBuilder}
            />
            <NetworkDetailsStep />
            <NetworkAssetsStep />
          </div>
        </Panel>
      </div>
    </Page>
  );
};

export const CreateNetworkPage = () => {
  const { mode } = useParams<{ mode: AssetCreateModeType }>();
  const navigate = useNavigate();
  const [areTemplatedConfigurationsAvailable] = useIsFeatureAvailable({
    featureFlagQuery: "core:templated_configurations",
  });

  const shouldUseAssetBuilder =
    areTemplatedConfigurationsAvailable &&
    mode === AssetCreateMode.ASSET_BUILDER;

  const onCancel = () => navigate({ pathname: NetworkListRoute.toLink() });

  const createNetwork = useCreateNetwork({
    onSuccess: (data: AssetGroupType) =>
      navigate({
        pathname: NetworkDetailRoute.toLink({
          pathParams: { networkId: data?.id },
        }),
      }),
  });

  const customAttributes =
    useListCustomAttributes({ filters: { entity_type: AssetType.ASSET_GROUP } })
      .data?.data ?? [];

  const onSubmit = (
    values: Array<Partial<AssetGroupCreateOneRequestType["data"]>>
  ) => {
    const allValues = values.reduce(
      (total, current) => ({ ...total, ...current }),
      {}
    );

    allValues.custom_attributes = sanitizeCustomAttributeFormInputs(
      allValues?.custom_attributes ?? {},
      customAttributes
    );

    createNetwork.mutate({
      data: allValues as AssetGroupCreateOneRequestType["data"],
    });
  };

  return (
    <MultiStepFormProvider
      steps={[
        {
          label: "Overview",
        },
        {
          label: "Details",
        },
        {
          label: "Assets",
        },
      ]}
      onCancel={onCancel}
      onSubmit={onSubmit}
      orientation="vertical"
      onSubmitMutationState={{
        isSubmitting: createNetwork.isLoading,
      }}
      submissionActionText="Create"
    >
      <CreateNetworkPageContent shouldUseAssetBuilder={shouldUseAssetBuilder} />
    </MultiStepFormProvider>
  );
};
